import { Component, OnInit } from '@angular/core';
import { UserService, PageableUsersData } from '../../services/user.service';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { User } from '../../types/user-type.type';
import { UserRoleType } from '../../types/user-role.type';
import { BasicUser } from '../../services/user.service';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

interface UserUpdateRequest {
  id: number;
  email?: string;
  cpf?: string;
  name: string;
  shortName: string;
  role: string;
}

@Component({
  selector: 'app-users',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    TableModule,
    PaginatorModule,
    ToastModule,
  ],
  template: `
    <p-toast></p-toast>
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2 class="card-title"><i class="pi pi-users"></i> Gerenciamento de Usuários</h2>
        </div>
      </ng-template>

      <div *ngIf="!selectedUser">
        <form [formGroup]="filterForm" (ngSubmit)="searchRegisters()">
          <div class="filter-inputs">
            <div class="form-field">
              <label class="field-label">Usuário</label>
              <p-dropdown
                formControlName="userId"
                [options]="allUsers"
                optionLabel="shortName"
                optionValue="id"
                placeholder="Selecione o usuário"
                [filter]="true"
                filterBy="shortName"
                styleClass="w-full"
                [showClear]="true">
              </p-dropdown>
            </div>

            <div class="form-field">
              <label class="field-label">Acesso</label>
              <p-dropdown
                formControlName="role"
                [options]="roleOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Selecione o acesso"
                styleClass="w-full"
                [showClear]="true">
              </p-dropdown>
            </div>

            <div class="form-field button-field">
              <p-button
                label="Buscar"
                icon="pi pi-search"
                type="submit"
                styleClass="search-button"
                [style]="{'width': '100%'}">
              </p-button>
            </div>

            <div class="form-field button-field">
              <p-button
                label="Limpar"
                icon="pi pi-times"
                type="button"
                severity="secondary"
                (onClick)="clearFilters()"
                [style]="{'width': '100%'}">
              </p-button>
            </div>
          </div>
        </form>
      </div>

      <div *ngIf="selectedUser" class="selected-user">
        <h3 class="section-title">Usuário Selecionado</h3>
        <form [formGroup]="userForm" (ngSubmit)="setUser()">
          <div class="form-grid">
            <div class="form-field">
              <label class="field-label">Nome Curto</label>
              <input
                pInputText
                formControlName="shortName"
                readonly
                class="w-full"
              />
            </div>

            <div class="form-field">
              <label class="field-label">Nome Completo</label>
              <input
                pInputText
                formControlName="name"
                readonly
                class="w-full"
              />
            </div>

            <div class="form-field">
              <label class="field-label">CPF</label>
              <input
                pInputText
                formControlName="cpf"
                readonly
                class="w-full"
              />
            </div>

            <div class="form-field">
              <label class="field-label">Email</label>
              <input
                pInputText
                formControlName="email"
                readonly
                class="w-full"
              />
            </div>

            <div class="form-field">
              <label class="field-label">Acesso</label>
              <p-dropdown
                formControlName="role"
                [options]="roles"
                optionLabel="description"
                optionValue="name"
                placeholder="Selecione o acesso"
                styleClass="w-full">
              </p-dropdown>
            </div>
          </div>

          <div class="button-group">
            <p-button
              label="Voltar"
              icon="pi pi-arrow-left"
              severity="secondary"
              type="button"
              (onClick)="clearSelection()">
            </p-button>
            <p-button
              label="Alterar"
              icon="pi pi-check"
              type="submit">
            </p-button>
          </div>
        </form>
      </div>

      <div class="results-container" *ngIf="!selectedUser && users.length > 0">
        <p-table
          [value]="users"
          [tableStyle]="{'min-width': '50rem'}"
          styleClass="p-datatable-sm p-datatable-striped">
          <ng-template pTemplate="header">
            <tr>
              <th>Nome Curto</th>
              <th>Nome Completo</th>
              <th>CPF</th>
              <th>Email</th>
              <th>Acesso</th>
              <th>Ações</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-user>
            <tr>
              <td>{{ user.shortName }}</td>
              <td>{{ user.name }}</td>
              <td>{{ user.cpf }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.role }}</td>
              <td>
                <p-button
                  icon="pi pi-pencil"
                  severity="info"
                  [rounded]="true"
                  [text]="true"
                  (onClick)="selectUser(user)"
                  pTooltip="Editar usuário">
                </p-button>
              </td>
            </tr>
          </ng-template>
        </p-table>

        <p-paginator
          [rows]="pageSize"
          [totalRecords]="totalUsers"
          [first]="currentPage * pageSize"
          [rowsPerPageOptions]="[5, 10, 25, 100]"
          (onPageChange)="onPageChange($event)">
        </p-paginator>
      </div>
    </p-card>
  `,
  styles: [
    `
      .card-header {
        padding: 1.5rem;
      }

      .card-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #325565;
      }

      .filter-inputs {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 1rem;
        margin-bottom: 1.5rem;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .field-label {
        font-size: 0.875rem;
        font-weight: 500;
        color: #4b5563;
      }

      .button-field {
        padding-top: 1.75rem;
      }

      ::ng-deep .search-button {
        background-color: rgb(16, 148, 158) !important;
        border-color: rgb(16, 148, 158) !important;
        height: 42px;
      }

      ::ng-deep .search-button:hover {
        background-color: rgb(14, 130, 140) !important;
        border-color: rgb(14, 130, 140) !important;
      }

      ::ng-deep .p-inputtext,
      ::ng-deep .p-dropdown {
        height: 42px;
      }

      ::ng-deep .w-full {
        width: 100%;
      }

      .selected-user {
        background-color: #f9fafb;
        padding: 1.5rem;
        border-radius: 6px;
        margin-bottom: 1.5rem;
      }

      .section-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #325565;
        margin-bottom: 1rem;
      }

      .form-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
        margin-bottom: 1.5rem;
      }

      .button-group {
        display: flex;
        gap: 0.75rem;
        justify-content: flex-end;
      }

      .results-container {
        margin-top: 1.5rem;
      }

      ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        background-color: transparent;
        color: #4b5563;
        font-weight: 600;
        border-bottom: 2px solid #e5e7eb;
        padding: 0.75rem;
      }

      ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
        padding: 0.75rem;
        border-bottom: 1px solid #e5e7eb;
      }

      ::ng-deep .p-paginator {
        margin-top: 1rem;
        border: none;
        background: transparent;
      }

      @media (max-width: 768px) {
        .filter-inputs {
          grid-template-columns: 1fr;
        }

        .form-grid {
          grid-template-columns: 1fr;
        }

        .button-group {
          flex-direction: column;
        }

        ::ng-deep .p-datatable {
          font-size: 0.875rem;
        }
      }
    `,
  ],
})
export class UserComponent implements OnInit {
  users: User[] = [];
  roles: { id: number; name: string; description: string }[] = [];
  selectedUser: User | null = null;
  totalUsers: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  userForm: FormGroup;
  allUsers: BasicUser[] = [];

  filterForm: FormGroup;

  roleOptions = [
    { value: '', label: '' },
    ...Object.keys(UserRoleType).map((key) => ({
      value: key,
      label: UserRoleType[key as keyof typeof UserRoleType],
    })),
  ];

  constructor(
    private userService: UserService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    this.userForm = this.fb.group({
      shortName: ['', Validators.required],
      name: ['', Validators.required],
      cpf: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['', Validators.required],
    });

    this.filterForm = this.fb.group({
      userId: [''],
      role: [''],
    });
  }

  ngOnInit(): void {
    this.loadAllUsers();
    this.loadRoles();
    this.searchRegisters();
  }

  loadAllUsers(): void {
    this.userService.getUsers().subscribe({
      next: (response) => {
        this.allUsers = response.data.sort((a, b) =>
          a.shortName.localeCompare(b.shortName)
        );
      },
      error: (error) => console.error('Error loading users:', error),
    });
  }

  loadRoles() {
    this.userService.getRoles().subscribe({
      next: (response) => {
        if (response?.data) {
          this.roles = response.data.sort((a, b) =>
            a.description.localeCompare(b.description)
          );
        }
      },
      error: (error) => console.error('Error loading roles:', error),
    });
  }

  searchRegisters() {
    const { userId, role } = this.filterForm.value;
    this.userService
      .getUsersByFilter(
        userId ? Number(userId) : undefined,
        role ? role : undefined,
        this.currentPage
      )
      .subscribe({
        next: (data) => {
          this.users = data.content.sort((a, b) =>
            a.shortName.localeCompare(b.shortName)
          );
          this.totalUsers = data.totalElements;
        },
        error: (error) => this.handleError('Erro na busca', error),
      });
  }

  selectUser(user: User): void {
    this.userService.getUserById(user.id).subscribe({
      next: (user) => {
        this.selectedUser = user;
        this.userForm.patchValue({
          shortName: user.shortName || '',
          name: user.name || '',
          cpf: user.cpf || '',
          email: user.email || '',
          role: user.role || '',
        });
      },
      error: (error) => this.handleError('Erro ao carregar usuário', error),
    });
  }

  setUser() {
    if (this.userForm.invalid || !this.selectedUser) return;

    this.userService
      .setUserById(this.selectedUser.id, this.userForm.value)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Usuário atualizado com sucesso!',
            life: 3000
          });
          this.clearSelection();
          this.searchRegisters();
        },
        error: (error) => this.handleError('Erro ao atualizar usuário', error),
      });
  }

  clearFilters() {
    this.filterForm.reset();
    this.searchRegisters();
  }

  clearSelection(): void {
    this.selectedUser = null;
  }

  onPageChange(event: any): void {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.searchRegisters();
  }

  private handleError(message: string, error: any) {
    console.error(message, error);
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail: message,
      life: 3000
    });
  }
}
